import { defineStore } from 'pinia';
import { MANUSCRIPT_V1_API_URL_PREFIX as URL_PREFIX } from '@/lib/constants/manuscripts/api';
import * as action from '@/lib/constants/manuscripts/store';
import { USER_COLORS } from '@/lib/constants';
import { tokens as nebulaTokens } from '@discoveryedu/nebula-core/dist/data/nebula_tokens.json';
import { useUserStore } from '@/stores';

const useManuscriptStore = defineStore(
  'manuscript-manuscript',

  {
    state: () => ({
      assigneesList: [],
      isProcessingRequest: false,
      manuscript: null,
      manuscripts: [],
      searchText: '',
      searchParams: {
        // Default search params
        sort_field: 'created_on',
        sort_direction: 'desc',
        page: 1,
        per_page: 25,
        assignee_id: [],
        project_id: [],
        progress_status_number: [],
      },
      selectedManuscriptIds: [],
      statusCodes: [],
      lessonTypes: [],
      pagination: {},
      usersAssignedAColor: {},
    }),

    getters: {
      // TODO: Implement other getters as needed.
      searchParamsOptions(state) {
        // Filter out empty values except for pagination and sorting params
        const paramsWithValue = Object.fromEntries(
          Object.entries({ ...state.searchParams, search_text: state.searchText }).filter(([key, value]) => (key.length && Boolean(value.length))
              || ['page', 'per_page', 'sort_field', 'sort_direction'].includes(key)),
        );
        // Then flatten array values for URLSearchParams
        const flatParams = Object.entries(paramsWithValue).flatMap(
          ([key, value]) => {
            if (Array.isArray(value)) {
              return value.map((v) => [key, v]);
            }
            return [[key, value]];
          });
        // Then set array for key/value pairs
        const searchParams = new URLSearchParams(flatParams);
        return {
          string: searchParams.toString(),
          object: paramsWithValue,
        };
      },
      checkAllCheckboxIsChecked(state) {
        if (!state.manuscripts.length) return false;
        const currentPageManuscriptIds = state.manuscripts.map((manuscript) => manuscript.manuscript_id);
        // If maximum selectable manuscripts is reached.
        if (state.selectedManuscriptIds.length === action.MAXIMUM_SELECTABLE_MANUSCRIPTS) {
          return currentPageManuscriptIds.some((manuscriptId) => this.selectedManuscriptIds.includes(manuscriptId));
        }

        // If max is not reached AND per_page is <= maximum:
        if (state.searchParams.per_page < action.MAXIMUM_SELECTABLE_MANUSCRIPTS) {
          // Check current page has all manuscripts in selectedManuscriptIds, return true else false
          return currentPageManuscriptIds.every((manuscriptId) => this.selectedManuscriptIds.includes(manuscriptId));
        }

        // If max is NOT reached AND per_page is > maximum, check-all should only be checked if all
        // currentPageManuscriptIds are in the selectedManuscriptIds
        return false;
      },
      hasFiltersApplied({ searchParams }) {
        return Object.entries(searchParams).some(([key, value]) => !['page', 'per_page', 'sort_field', 'sort_direction'].includes(key) && !!value.length);
      },
    },

    actions: {
      [action.SET_CURRENT_PAGE](page) {
        this.searchParams.page = page;
      },

      [action.SET_FILTER_BY_SEARCH_TEXT](searchText) {
        this.searchText = searchText;
      },

      [action.SET_FILTER_BY_ASSIGNEE_IDS](assigneeIds) {
        this.searchParams.assignee_id = assigneeIds;
      },

      [action.SET_FILTER_BY_PROJECT_IDS](projectIds) {
        this.searchParams.project_id = projectIds;
      },

      [action.SET_FILTER_BY_STATUSES](statuses) {
        this.searchParams.progress_status_number = statuses;
      },

      [action.SET_SORT_BY](sortBy) {
        this.searchParams.sort_field = sortBy;
      },

      [action.SET_SORT_DIRECTION](sortDirection) {
        this.searchParams.sort_direction = sortDirection;
      },

      [action.SET_MANUSCRIPTS_PER_PAGE](perPage) {
        this.searchParams.page = 1; // Reset to first page when changing per_page
        this.searchParams.per_page = perPage;
      },

      [action.RESET_ALL_FILTERS]() {
        this.searchText = '';
        this.searchParams = {
          // Default search params
          sort_field: 'created_on',
          sort_direction: 'desc',
          page: 1,
          per_page: 25,
          assignee_id: [],
          project_id: [],
          progress_status_number: [],
        };
      },

      [action.SET_INITIAL_SEARCH_PARAMS](query) {
        const {
          page,
          per_page: perPage,
          sort_field: sortBy,
          sort_direction: sortDirection,
          search_text: searchText,
          assignee_id: assigneeIds,
          project_id: projectIds,
          progress_status_number: status,
        } = query;
        const pageLoadParams = {
          searchParams: {
            ...query,
            page: page || this.searchParams.page,
            per_page: perPage || this.searchParams.per_page,
            sort_field: sortBy || this.searchParams.sort_field,
            sort_direction: sortDirection || this.searchParams.sort_direction,
            ...(assigneeIds?.length ? { assignee_id: Array.isArray(assigneeIds) ? assigneeIds : [assigneeIds] } : {}),
            ...(projectIds?.length ? { project_id: Array.isArray(projectIds) ? projectIds : [projectIds] } : {}),
            ...(status?.length ? { progress_status_number: Array.isArray(status) ? status : [status] } : {}),
          },
          ...(searchText?.length ? { searchText } : {}),
        };
        this.$patch(pageLoadParams);
      },

      [action.SET_SELECTED_MANUSCRIPT_IDS](manuscriptList) {
        this.selectedManuscriptIds = manuscriptList;
      },

      [action.UPDATE_USERS_ASSIGNED_A_COLOR]() {
        this.manuscripts.forEach(({ assignee }) => {
          if (!!assignee.id && !this.usersAssignedAColor[assignee.id]) {
            // Only set a random color if the user has not been cached yet.
            const hasName = !!assignee.first_name || !!assignee.last_name;
            const randomIndex = Math.floor(Math.random() * (USER_COLORS.length - 1));
            const backgroundColor = hasName ? (USER_COLORS[randomIndex] || USER_COLORS[0]) : nebulaTokens.color.platform.neutral['1000'];

            // Cache the color with the user ID as the key.
            this.usersAssignedAColor[assignee.id] = backgroundColor;
          }
        });
      },

      [action.CLEAR_MANUSCRIPT]() {
        this.manuscript = null;
      },

      async [action.DELETE_MANUSCRIPT](manuscriptId) {
        this.isProcessingRequest = true;

        try {
          await this.appApi.delete(
            `${URL_PREFIX}/manuscripts/${manuscriptId}`,
          );
          return true;
        } catch (error) {
          window.console.error('🏪 Manuscript Store: Failed to delete manuscript', error);
          return false;
        } finally {
          this.isProcessingRequest = false;
        }
      },

      async [action.GET_MANUSCRIPT](manuscriptId) {
        // Check cache first
        if (this.manuscript?.manuscript_id === manuscriptId) {
          window.console.log('📋 Manuscript: Using cached data for', manuscriptId);
          return this.manuscript;
        }

        this.isProcessingRequest = true;

        try {
          const response = await this.appApi(`${URL_PREFIX}/manuscripts/${manuscriptId}`);
          this.manuscript = response.data;
          window.console.log('📋 Manuscript data received:', this.manuscript);
          return this.manuscript;
        } catch (error) {
          window.console.error('🏪 Manuscript Store: API call failed', error);
          this.manuscript = null;
          return null;
        } finally {
          this.isProcessingRequest = false;
        }
      },

      async [action.PATCH_MANUSCRIPT](manuscriptId, payload) {
        this.isProcessingRequest = true;

        try {
          const response = await this.appApi(
            `${URL_PREFIX}/manuscripts/${manuscriptId}`,
            {
              method: 'PATCH',
              body: payload,
            },
          );

          // Update local manuscript state with updated manuscript
          this.manuscript = response.data;

          // Return the updated manuscript
          return { success: true, data: response.data };
        } catch (error) {
          console.error('Error patching manuscript:', error);
          console.error('Error response:', error.response);
          console.error('Error data:', error.response?.data);
          console.error('Payload sent:', payload);
          return {
            success: false,
            error: error.response?.data || error.message,
          };
        } finally {
          this.isProcessingRequest = false;
        }
      },

      async [action.POST_MANUSCRIPT](payload) {
        this.isProcessingRequest = true;

        try {
          const response = await this.appApi(
            `${URL_PREFIX}/manuscripts/`,
            {
              method: 'POST',
              body: payload,
            },
          );

          // Update local manuscript state with created manuscript
          this.manuscript = response.data;

          // Refresh manuscripts list
          this[action.GET_MANUSCRIPTS]();

          // Return the created manuscript
          return { success: true, data: response.data };
        } catch (error) {
          console.error('Error creating manuscript:', error);
          return {
            success: false,
            error: error.response?.data || error.message,
          };
        } finally {
          this.isProcessingRequest = false;
        }
      },

      async [action.GET_MANUSCRIPTS]() {
        this.isProcessingRequest = true;
        let url = `${URL_PREFIX}/manuscripts/`;

        if (this.searchParamsOptions.string.length) {
          url += `?${this.searchParamsOptions.string}`;
        }

        try {
          const response = await this.appApi(url);
          const { data: { results, pagination } } = response;

          this.manuscripts = results;
          this.pagination = pagination;

          // Assign a color to each user and cache it the store.
          this.updateUsersAssignedAColor();

          return response.data;
        } catch (error) {
          console.error('Error fetching manuscripts:', error);
          return error;
        } finally {
          this.isProcessingRequest = false;
        }
      },

      async [action.GET_MANUSCRIPT_USERS]() {
        const url = `${URL_PREFIX}/users/`;
        try {
          const response = await this.appApi(url);
          const { data: { users } } = response;
          this.assigneesList = users;
          return response.data;
        } catch (error) {
          console.error('Error fetching manuscript users:', error);
          return error;
        }
      },

      async [action.GET_MANUSCRIPT_PRODUCT_CONFIG]() {
        const url = `${URL_PREFIX}/product_config/`;
        try {
          const response = await this.appApi(url);
          const { data } = response;
          this.statusCodes = data.product_configs[0].progress_statuses;
          this.lessonTypes = data.product_configs[0].types;
          return data;
        } catch (error) {
          console.error('Error fetching manuscript: ', error);
          return error;
        }
      },

      async [action.POST_EXPORTS](payload) {
        this.isProcessingRequest = true;

        const userStore = useUserStore();
        try {
          const response = await this.appApi(
            `${URL_PREFIX}/exports/`,
            {
              method: 'POST',
              body: payload,
              headers: {
                'X-Token': userStore.user.token,
              },
            },
          );

          return { success: true, data: response.data };
        } catch (error) {
          console.error('Error posting manuscript: ', error);
          return {
            success: false,
            error: error.response?.data || error.message,
          };
        } finally {
          this.isProcessingRequest = false;
        }
      },
    },
  },
);

export default useManuscriptStore;
